import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Send, CheckCircle2, XCircle, Clock, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import alertService from '../../../utils/alert';
import { Button } from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const POLL_MS = 3000;
const POLL_MAX_MS = 3 * 60 * 1000;

const bodyVarCount = (tpl) => {
  const body = (tpl.components || []).find((c) => c.type === 'BODY');
  return new Set(((body && body.text) || '').match(/\{\{\s*\d+\s*\}\}/g) || []).size;
};

// Why the test sender cannot send a given template (mirrors the server), or null when it can.
function unsupportedReason(tpl) {
  const header = (tpl.components || []).find((c) => c.type === 'HEADER');
  const buttons = (tpl.components || []).find((c) => c.type === 'BUTTONS')?.buttons || [];
  if (header?.format && header.format !== 'TEXT') return 'has a media header';
  if (header && /\{\{/.test(header.text || '')) return 'has a header variable';
  if (buttons.some((b) => /\{\{/.test(b.url || '') || ['OTP', 'FLOW', 'COPY_CODE', 'CATALOG', 'MPM'].includes(b.type))) return 'has a dynamic button';
  return null;
}

function Step({ label, at, state }) {
  const color = state === 'done' ? '#2E844A' : state === 'failed' ? '#BA0517' : '#9A9894';
  const Icon = state === 'done' ? CheckCircle2 : state === 'failed' ? XCircle : Clock;
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <Icon size={16} style={{ color }} />
      <span className="font-semibold" style={{ color: state === 'pending' ? '#706E6B' : '#080707' }}>{label}</span>
      {at && <span className="text-[#706E6B]">{new Date(at).toLocaleTimeString()}</span>}
    </div>
  );
}

export default function SendTestCard({ isAdmin, connected }) {
  const [templates, setTemplates] = useState([]);
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [selected, setSelected] = useState('');
  const [vars, setVars] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [live, setLive] = useState(null);
  const [pollDone, setPollDone] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!connected) return;
    let alive = true;
    setLoadingTpl(true);
    api.get('/meta/templates')
      .then((res) => { if (alive) setTemplates((res.data.templates || []).filter((t) => t.status === 'APPROVED')); })
      .catch(() => { if (alive) setTemplates([]); })
      .finally(() => { if (alive) setLoadingTpl(false); });
    return () => { alive = false; };
  }, [connected]);

  const tpl = useMemo(() => templates.find((t) => `${t.name}|${t.language}` === selected), [templates, selected]);
  const blocked = tpl ? unsupportedReason(tpl) : null;
  const varCount = tpl ? bodyVarCount(tpl) : 0;

  useEffect(() => { setVars(Array.from({ length: varCount }, () => '')); }, [selected, varCount]);

  // Follow the message while Meta's status webhooks update it: sent → delivered → read, or failed with a reason.
  useEffect(() => {
    clearInterval(timer.current);
    if (!result?.ok || !result.messageDbId) return undefined;
    const started = Date.now();
    setLive(null);
    setPollDone(false);
    const tick = async () => {
      try {
        const res = await api.get(`/meta/diagnostics/messages/${result.messageDbId}`);
        setLive(res.data);
        // Keep following after "delivered" so a later "read" shows up; stop on a final state or after 3 minutes.
        if (['read', 'failed'].includes(res.data.status)) { clearInterval(timer.current); setPollDone(true); }
      } catch { /* keep polling */ }
      if (Date.now() - started > POLL_MAX_MS) { clearInterval(timer.current); setPollDone(true); }
    };
    tick();
    timer.current = setInterval(tick, POLL_MS);
    return () => clearInterval(timer.current);
  }, [result]);

  const send = async () => {
    const digits = recipient.replace(/\D/g, '');
    if (digits.length < 8) return alertService.toast('error', 'Enter the recipient with country code, e.g. 919876543210');
    if (!tpl) return alertService.toast('error', 'Choose an approved template');
    if (vars.some((v) => !v.trim())) return alertService.toast('error', 'Fill in every template variable');
    const ok = await alertService.confirm('Send a real WhatsApp message?', `Template "${tpl.name}" will be sent to +${digits}. It counts toward your plan and may be billed by Meta.`, 'warning', 'Send test');
    if (!ok) return;
    setSending(true);
    setResult(null);
    setLive(null);
    try {
      const res = await api.post('/meta/diagnostics/send-test', { recipient: digits, templateName: tpl.name, language: tpl.language, bodyParams: vars });
      setResult(res.data);
    } catch (err) {
      alertService.toast('error', err.response?.data?.error || 'Could not send the test message');
    } finally {
      setSending(false);
    }
  };

  const delivered = live && ['delivered', 'read'].includes(live.status);
  const failedLive = live?.status === 'failed';

  return (
    <div className="sf-card">
      <div className="sf-card-header">
        <h3 className="sf-card-title flex items-center gap-2"><Send className="w-4 h-4 text-[#0176D3]" />Send a test message</h3>
      </div>
      <div className="sf-card-body space-y-4">
        <p className="text-[12px] text-[#706E6B]">
          Sends one approved template straight to Meta using your saved credentials and shows Meta's exact answer. Use your own number first.
          "Accepted" only means Meta took the message — the live status below shows whether it was actually delivered.
        </p>

        {!connected ? (
          <p className="text-[12px] text-[#8C4B02]">Connect WhatsApp in API Configuration first.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="space-y-1.5 block">
                <span className="text-[11px] font-bold text-[#706E6B]">Recipient (country code, digits only)</span>
                <input value={recipient} onChange={(e) => setRecipient(e.target.value)} inputMode="numeric" placeholder="919876543210"
                  className="w-full px-3 h-10 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px] focus:bg-white focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none" />
              </label>
              <label className="space-y-1.5 block">
                <span className="text-[11px] font-bold text-[#706E6B]">Approved template {loadingTpl && '(loading…)'}</span>
                <select value={selected} onChange={(e) => setSelected(e.target.value)}
                  className="w-full px-3 h-10 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px] focus:bg-white focus:border-[#0176D3] outline-none">
                  <option value="">{templates.length ? 'Choose a template…' : 'No approved templates synced'}</option>
                  {templates.map((t) => <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>{t.name} ({t.language}){unsupportedReason(t) ? ' — not supported here' : ''}</option>)}
                </select>
              </label>
            </div>

            {tpl && (
              <div className="rounded border border-[#D8DDE6] bg-[#F4F6F9] p-3 text-[12px] text-[#080707] whitespace-pre-wrap">
                {(tpl.components || []).find((c) => c.type === 'BODY')?.text || '(no body text)'}
              </div>
            )}
            {blocked && <p className="text-[12px] text-[#8C4B02]">This template {blocked}, which the test sender can't fill in. Test it from Direct Signal instead.</p>}

            {tpl && !blocked && varCount > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {vars.map((v, i) => (
                  <label key={i} className="space-y-1.5 block">
                    <span className="text-[11px] font-bold text-[#706E6B]">{`Variable {{${i + 1}}}`}</span>
                    <input value={v} onChange={(e) => setVars((a) => a.map((x, j) => (j === i ? e.target.value : x)))}
                      className="w-full px-3 h-9 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px] focus:bg-white focus:border-[#0176D3] outline-none" />
                  </label>
                ))}
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button onClick={send} loading={sending} disabled={!isAdmin || !tpl || !!blocked} icon={Send} title={isAdmin ? undefined : 'Only a company admin can send a test message'}>
                Send test message
              </Button>
              {!isAdmin && <span className="text-[11px] text-[#706E6B]">Admins only</span>}
            </div>
          </>
        )}

        {result && result.ok && (
          <div className="rounded border border-[#C4DCC8] bg-[#EBF3ED] p-4 space-y-3">
            <div className="flex items-center gap-2 text-[13px] font-bold text-[#2E844A]"><CheckCircle2 size={16} />Accepted by Meta</div>
            <p className="text-[12px] text-[#080707]">{result.note}</p>
            <p className="text-[11px] font-mono text-[#706E6B] break-all">Message ID {result.wamid}{result.request?.durationMs != null && ` · ${result.request.durationMs} ms`}</p>
            <div className="space-y-1.5 pt-1">
              <Step label="Accepted by Meta" at={live?.sentAt} state="done" />
              <Step label="Delivered to the phone" at={live?.deliveredAt} state={delivered ? 'done' : failedLive ? 'failed' : 'pending'} />
              <Step label="Read" at={live?.readAt} state={live?.status === 'read' ? 'done' : 'pending'} />
            </div>
            {!pollDone && !delivered && !failedLive && <p className="flex items-center gap-2 text-[11px] text-[#706E6B]"><Loader2 size={12} className="animate-spin" />Waiting for Meta's delivery receipt…</p>}
            {pollDone && !delivered && !failedLive && (
              <p className="text-[12px] text-[#8C4B02]">No delivery receipt after 3 minutes. The phone may be off, or Meta's status webhooks are not reaching the CRM — check the Webhooks group above.</p>
            )}
            {failedLive && (
              <div className="rounded border border-[#F6A3AB] bg-[#FDE7E9] p-3 text-[12px] text-[#8E030F] space-y-1">
                <p className="font-bold">{live.explanation?.title || 'Delivery failed'}{live.error?.code ? ` (code ${live.error.code})` : ''}</p>
                <p>{live.error?.message}</p>
                {live.explanation && <p><span className="font-bold">What to do: </span>{live.explanation.fix}</p>}
              </div>
            )}
          </div>
        )}

        {result && !result.ok && (
          <div className={cn('rounded border border-[#F6A3AB] bg-[#FDE7E9] p-4 space-y-2')}>
            <div className="flex items-center gap-2 text-[13px] font-bold text-[#BA0517]"><XCircle size={16} />Meta rejected the message</div>
            <p className="text-[12px] text-[#080707] break-words">{result.message}</p>
            {result.explanation && (
              <div className="text-[12px] text-[#080707] space-y-1">
                <p><span className="font-bold">{result.explanation.title}. </span>{result.explanation.cause}</p>
                <p><span className="font-bold">What to do: </span>{result.explanation.fix}</p>
              </div>
            )}
            <p className="text-[11px] font-mono text-[#706E6B] break-all">
              {result.error?.code != null && <>code {result.error.code}{result.error.subcode ? ` / ${result.error.subcode}` : ''}</>}
              {result.error?.fbtraceId && <> · fbtrace {result.error.fbtraceId}</>}
              {result.request?.httpStatus != null && <> · HTTP {result.request.httpStatus}</>}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
